/**
 * feat-298 — the body of `/quiz-review`: Track headers, Lesson and Drill
 * banners, stub rows, and the per-item layout wrappers.
 *
 * `Layout`, `ItemCells` and `BannerSplit` moved here unchanged from
 * `QuizContentReview.tsx` (feat-284); their comments are the alignment contract
 * for Both mode. The item renderers themselves are the per-item cards in this
 * directory.
 */
import React, { useCallback, useContext, useState } from 'react';
import { ArrowUpRight, Eye } from 'lucide-react';
import {
  QuizLesson,
  QuizUnit,
  ReadingItemSummary,
} from '../../../services/quizContentAdminService';
import { termCount } from '../quizTerminology';
import { interleaveItems, LessonItem } from '../QuizContentManagement';
import { resolveField } from '../quizReviewLanguage';
import { Column, FieldRun } from './shared';
import QuestionCard from './QuestionCard';
import RehearsalCard from './RehearsalCard';
import ReadingCard from './ReadingCard';
import VideoCard from './VideoCard';
import { joinSummary, QUIZ_REVIEW_NAV_COPY as COPY } from './navCopy';
import { DisclosureButton, FOCUS_RING, regionId } from './Collapsible';
import { GroupedCurriculum, LessonGroup, StubPlacement } from './groupByTrack';
import { drillKey, lessonKey, NAV_TARGET_ATTR, trackKey } from './reviewKeys';
import {
  ItemPendingChip,
  LessonIdContext,
  LessonPendingChip,
  useLessonId,
  useReviewEdit,
} from './reviewEdit';
import InlineEditable from './InlineEditable';
import { EditLine } from './editCoordinator';
import { FIELD_LABELS, QUIZ_REVIEW_EDIT_COPY as EDIT } from './editCopy';
import { audienceOf, Audience } from '../../../services/quizContentAdminService';
import AudienceChip from './audience/AudienceChip';
import { useAudience } from './audience/audienceContext';
import { isEveryone } from './audience/audienceFormat';
import { AUDIENCE_COPY } from './audience/audienceCopy';

/**
 * feat-284 — how one curriculum item is laid out.
 *
 * `single` renders one column in `column`'s language. `both` renders the English
 * and translated cells as ADJACENT CHILDREN OF ONE CSS GRID — that adjacency is
 * the entire alignment mechanism, because grid makes both cells of a row the same
 * height and puts their top edges on the same y. Two independently-flowing
 * columns would drift apart down the page; here any height mismatch is absorbed
 * at the item boundary and reset by the next row.
 */
export interface Layout {
  mode: 'single' | 'both';
  /** The translated language; `null` in EN mode. */
  column: Column;
}

/**
 * The per-item wrapper: spacing, the Both-mode cell pair, and the scroll anchor.
 *
 * `data-item-key` goes on the outermost element of every unit banner, lesson
 * banner, question, rehearsal, video and reading — and in Both mode on the
 * ENGLISH (left) cell, so the SAME key exists in all three modes and EN → Both →
 * ES all anchor to the same element (design notes §4).
 *
 * Below `lg` Both does not attempt two columns: it collapses to one and renders
 * the pair STACKED, English then translated, separated by a top hairline.
 * Squeezing two columns into <1024 px makes both unreadable; stacking keeps
 * alignment trivially correct and nothing is squeezed.
 */
export const ItemCells: React.FC<{
  itemKey: string;
  layout: Layout;
  render: (column: Column, fullHeight: boolean) => React.ReactNode;
  /** feat-300: this item is a `revealItem` target — clear the sticky nav when
   *  scrolled to (only questions are, via the AI-edit counter). */
  jumpTarget?: boolean;
}> = ({ itemKey, layout, render, jumpTarget = false }) => {
  const mt = jumpTarget ? ` ${SCROLL_MT}` : '';
  if (layout.mode !== 'both') {
    return (
      <div data-item-key={itemKey} className={`mb-5${mt}`}>
        {render(layout.column, false)}
      </div>
    );
  }
  return (
    <>
      <div data-item-key={itemKey} className={`mb-0 lg:mb-5 lg:pr-6${mt}`}>
        {render(null, true)}
      </div>
      <div className="mb-5 lg:pl-6 mt-2 lg:mt-0 pt-2 lg:pt-0 border-t lg:border-t-0 lg:border-l border-softBorder">
        {render(layout.column, true)}
      </div>
    </>
  );
};

/**
 * A structural banner (unit, lesson) in Both mode.
 *
 * Banners SPAN the full width and split their translatable text with an inner
 * 50/50 grid, so the divider reads as one continuous vertical line down the page
 * rather than restarting at every banner. The inner divider uses the banner's own
 * tint (`border-sky-200`, `border-emerald-200`) rather than `softBorder`, so it
 * does not look like a crack in the banner.
 */
export const BannerSplit: React.FC<{
  layout: Layout;
  tint: string;
  leftPad: string;
  rightPad: string;
  render: (column: Column) => React.ReactNode;
}> = ({ layout, tint, leftPad, rightPad, render }) => {
  if (layout.mode !== 'both') return <>{render(layout.column)}</>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className={`${leftPad} lg:pr-6`}>{render(null)}</div>
      <div
        className={`${rightPad} lg:pl-6 mt-2 lg:mt-0 pt-2 lg:pt-0 border-t lg:border-t-0 lg:border-l ${tint}`}
      >
        {render(layout.column)}
      </div>
    </div>
  );
};


/**
 * A reading's item pair. The Show more / less state lives HERE — in the
 * `ItemCells` render scope, not in either cell — so in Both mode the single
 * toggle in the English cell expands both cells together, and shows if EITHER
 * cell overflows (NOTES §10). Cells stay `h-full`, so the row stays aligned.
 */
const ReadingItem: React.FC<{
  itemKey: string;
  rd: ReadingItemSummary;
  index: number;
  layout: Layout;
}> = ({ itemKey, rd, index, layout }) => {
  const [expanded, setExpanded] = useState(false);
  const [english, setEnglish] = useState(false);
  const [translated, setTranslated] = useState(false);
  const both = layout.mode === 'both';
  const overflows = english || (both && translated);
  const onEnglish = useCallback((o: boolean) => setEnglish(o), []);
  const onTranslated = useCallback((o: boolean) => setTranslated(o), []);
  return (
    <ItemCells
      itemKey={itemKey}
      layout={layout}
      render={(column, fullHeight) => {
        // In single mode the one cell is the "primary" one, whatever its language.
        const primary = !both || column === null;
        return (
          <ReadingCard
            rd={rd}
            index={index}
            column={column}
            fullHeight={fullHeight}
            expanded={expanded}
            onExpandedChange={setExpanded}
            showToggle={primary}
            overflows={overflows}
            onOverflowChange={primary ? onEnglish : onTranslated}
          />
        );
      }}
    />
  );
};

// ============================================================
// feat-298 — section controls (collapse + jump), supplied by the page
// ============================================================

export interface SectionControls {
  isOpen: (key: string) => boolean;
  /** `null` ⇒ sections are not collapsible and no toggle renders. */
  toggle: ((key: string) => void) | null;
  /** Scroll to a section by key, expanding its collapsed ancestors first. */
  jumpTo: (key: string) => void;
  /**
   * feat-300 — ADDITIVE. Expand a Drill (and its ancestors) and scroll one ITEM
   * in it (`data-item-key`) into view. Unlike `jumpTo` it moves no focus: the
   * caller decides where focus lands (the AI-edit counter focuses the item's
   * thread panel). Optional so every existing `SectionControls` value stays valid.
   */
  revealItem?: (drillKey: string, itemKey: string) => void;
}

export const SectionControlsContext = React.createContext<SectionControls>({
  isOpen: () => true,
  toggle: null,
  jumpTo: () => {},
});

/** `scroll-margin-top` for every jump target, so a plain
 *  `scrollIntoView({ block: 'start' })` lands below the sticky nav (NOTES §1). */
export const SCROLL_MT = 'scroll-mt-[calc(var(--review-chrome-top)+12px)]';

// ------------------------------------------------------------ feat-302 banner grid

/**
 * feat-302 (design NOTES §2 "Chip placement") — a banner that carries an
 * audience chip becomes a grid: disclosure | title block | chip at `sm+`; below
 * `sm` the chip drops to its own line under the title (`col-start-2`). ONE chip
 * element — only the grid changes. `withToggle` false drops the first column.
 */
export function bannerGrid(withToggle: boolean): string {
  return withToggle
    ? 'grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto] gap-x-2 items-start'
    : 'grid grid-cols-[minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-start';
}

/** The chip's grid cell. `smTop` aligns it with the heading's first line. */
export function chipCell(withToggle: boolean, smTop: string): string {
  return `${withToggle ? 'col-start-2' : 'col-start-1'} mt-1.5 sm:col-start-auto ${smTop} justify-self-start`;
}

/**
 * Whether a chip renders at all: never outside the page; in read-only (ES)
 * mode only when the item is scoped (NOTES §1).
 */
function useShowChip(audience: Audience, editable: boolean): boolean {
  const api = useAudience();
  return !!api && (editable || !isEveryone(audience));
}

/** NOTES §6.7 — the pinned "Saved. … no longer sees this" line. */
const EditedOutLine: React.FC<{ text: string }> = ({ text }) => (
  <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-ink" role="status">
    <Eye className="w-3.5 h-3.5 text-inkMuted" aria-hidden />
    {text}
  </p>
);

// ------------------------------------------------------------ summaries (NOTES §6)

function itemCount(d: QuizLesson): number {
  return (
    d.question_items.length +
    d.rehearsal_items.length +
    (d.video_items?.length ?? 0) +
    (d.reading_items?.length ?? 0)
  );
}

export function drillSummary(d: QuizLesson): string {
  const parts: string[] = [];
  if (d.question_items.length) parts.push(termCount(d.question_items.length, 'question'));
  if (d.rehearsal_items.length) parts.push(termCount(d.rehearsal_items.length, 'rehearsal'));
  if (d.video_items?.length) parts.push(termCount(d.video_items.length, 'video'));
  if (d.reading_items?.length) parts.push(termCount(d.reading_items.length, 'reading'));
  return parts.length ? joinSummary(parts) : COPY.noItemsYet;
}

export function lessonSummary(u: QuizUnit): string {
  const items = u.drills.reduce((n, d) => n + itemCount(d), 0);
  return joinSummary([termCount(u.drills.length, 'drill'), COPY.items(items)]);
}

/** Every Lesson in the Track's list, stubs included — the Track's real size. */
export function trackSummary(g: LessonGroup): string {
  const drills = g.entries.reduce((n, e) => n + e.lesson.drills.length, 0);
  return joinSummary([termCount(g.entries.length, 'lesson'), termCount(drills, 'drill')]);
}

// ------------------------------------------------------------ Drill

const DrillSection: React.FC<{
  drill: QuizLesson;
  index: number;
  layout: Layout;
  unitId: string;
}> = ({ drill, index, layout, unitId }) => {
  const { isOpen, toggle } = useContext(SectionControlsContext);
  const editApi = useReviewEdit();
  const lessonId = useLessonId();
  const key = drillKey(unitId, drill.id);
  const open = isOpen(key);
  const items = interleaveItems(drill);
  let qNum = 0;
  let rNum = 0;
  let vNum = 0;
  const numbered: Array<{ item: LessonItem; n: number }> = items.map((item) => {
    if (item.kind === 'question') {
      qNum += 1;
      return { item, n: qNum };
    }
    if (item.kind === 'rehearsal') {
      rNum += 1;
      return { item, n: rNum };
    }
    vNum += 1;
    return { item, n: vNum };
  });
  const both = layout.mode === 'both';
  const englishName = `${COPY.drillHeading(index)} ${drill.title}`;
  return (
    <div className={open ? 'mb-8' : 'mb-4'}>
      <div
        data-item-key={key}
        {...{ [NAV_TARGET_ATTR]: key }}
        className={`bg-emerald-50 border-l-4 border-emerald-300 py-2 rounded-r ${
          open ? 'mb-3' : 'mb-0'
        } ${both ? 'px-3 lg:px-0' : 'px-3'} ${SCROLL_MT}`}
      >
        <BannerSplit
          layout={layout}
          tint="border-emerald-200"
          leftPad="lg:pl-3"
          rightPad="lg:pr-3"
          render={(column) => {
            const title = resolveField(drill.title, drill.translations, column, 'title');
            // Toggle + summary in the ENGLISH cell only in Both mode (NOTES §6).
            const primary = !both || column === null;
            return (
              <div className="flex items-start gap-2">
                {primary && toggle && (
                  <DisclosureButton
                    open={open}
                    onToggle={() => toggle(key)}
                    controls={regionId(key)}
                    title={englishName}
                    className="-ml-1 w-6 h-6 text-emerald-800 hover:bg-emerald-100"
                    iconClassName="w-4 h-4"
                  />
                )}
                <div className="min-w-0 flex-1">
                  {editApi && column === null ? (
                    // feat-299: the English title is click-to-edit (Drill = `updateLesson`).
                    <EditLine
                      as="h4"
                      className="font-semibold text-ink break-words focus:outline-none"
                      headingProps={{
                        tabIndex: primary ? -1 : undefined,
                        'data-nav-heading': primary ? '' : undefined,
                      }}
                      prefix={
                        <>
                          {drill.emoji && <span className="mr-2">{drill.emoji}</span>}
                          {COPY.drillHeading(index)}
                        </>
                      }
                    >
                      <InlineEditable
                        value={drill.title ?? ''}
                        field="title"
                        testId={`inline-drill-title-${drill.id}`}
                        label={FIELD_LABELS.drillTitle()}
                        required
                        onSave={(next) => editApi.saveDrill(lessonId, drill.id, { title: next })}
                      />
                      <ItemPendingChip entity="learn_drill" id={drill.id} />
                      {!drill.active && (
                        <span className="ml-2 text-xs font-normal text-inkMuted">{COPY.inactive}</span>
                      )}
                    </EditLine>
                  ) : (
                    <h4
                      className="font-semibold text-ink break-words focus:outline-none"
                      tabIndex={primary ? -1 : undefined}
                      data-nav-heading={primary ? '' : undefined}
                    >
                      {drill.emoji && <span className="mr-2">{drill.emoji}</span>}
                      {COPY.drillHeading(index)} <FieldRun f={title} language={column ?? ''} />
                      {!drill.active && (
                        <span className="ml-2 text-xs font-normal text-inkMuted">{COPY.inactive}</span>
                      )}
                    </h4>
                  )}
                  {/* XP / questions-per-session is chrome: English in both cells. */}
                  <div className="text-xs text-inkMuted mt-0.5">
                    {drill.xp_reward} XP per completion ·{' '}
                    {COPY.questionsPerSession(drill.question_count)}
                  </div>
                  {primary && !open && (
                    <p className="mt-1 text-xs font-medium text-inkMuted">{drillSummary(drill)}</p>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>
      <div id={regionId(key)} className={open ? `pl-1${both ? ' grid grid-cols-1 lg:grid-cols-2' : ''}` : undefined}>
        {open &&
          numbered.map(({ item, n }) => {
            if (item.kind === 'question') {
              return (
                <ItemCells
                  key={`q-${item.q.id}`}
                  itemKey={`${key}-q${item.q.id}`}
                  layout={layout}
                  jumpTarget
                  render={(column) => <QuestionCard q={item.q} index={n} column={column} />}
                />
              );
            }
            if (item.kind === 'rehearsal') {
              return (
                <ItemCells
                  key={`r-${item.r.id}`}
                  itemKey={`${key}-r${item.r.id}`}
                  layout={layout}
                  render={(column, fullHeight) => (
                    <RehearsalCard r={item.r} index={n} column={column} fullHeight={fullHeight} />
                  )}
                />
              );
            }
            if (item.kind === 'video') {
              return (
                <ItemCells
                  key={`v-${item.v.id}`}
                  itemKey={`${key}-v${item.v.id}`}
                  layout={layout}
                  render={(_column, fullHeight) => (
                    <VideoCard v={item.v} index={n} fullHeight={fullHeight} />
                  )}
                />
              );
            }
            return (
              <ReadingItem
                key={`rd-${item.rd.id}`}
                itemKey={`${key}-rd${item.rd.id}`}
                rd={item.rd}
                index={n}
                layout={layout}
              />
            );
          })}
        {open && numbered.length === 0 && (
          // Chrome, English in every mode — and in Both it spans both columns.
          <div className={`text-sm text-inkMuted italic${both ? ' lg:col-span-2' : ''}`}>
            {COPY.emptyDrill}
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------ Lesson

export const LessonSection: React.FC<{ unit: QuizUnit; index: number; layout: Layout }> = ({
  unit,
  index,
  layout,
}) => {
  const { isOpen, toggle } = useContext(SectionControlsContext);
  const editApi = useReviewEdit();
  const key = lessonKey(unit.id);
  const open = isOpen(key);
  const both = layout.mode === 'both';
  const englishName = `${COPY.lessonHeading(index)} ${unit.title}`;
  // feat-302: the chip is editable wherever feat-299 inline edit is live (the
  // English cell), read-only in the ES-only cell (design Q1).
  const audienceApi = useAudience();
  const audience = audienceOf(unit);
  const editedOut = audienceApi?.editedOutLine('lesson', unit.id) ?? null;
  const showEditableChip = useShowChip(audience, true);
  const showReadonlyChip = useShowChip(audience, false);
  return (
    <div className={open ? 'mb-10' : 'mb-4'}>
      <div
        data-item-key={key}
        {...{ [NAV_TARGET_ATTR]: key }}
        className={`group/banner bg-sky-50 border-l-4 border-sky-400 py-3 rounded-r ${open ? 'mb-4' : 'mb-0'} ${
          both ? 'px-4 lg:px-0' : 'px-4'
        } ${SCROLL_MT}`}
      >
        <BannerSplit
          layout={layout}
          tint="border-sky-200"
          leftPad="lg:pl-4"
          rightPad="lg:pr-4"
          render={(column) => {
            const title = resolveField(unit.title, unit.translations, column, 'title');
            const subtitle = resolveField(unit.subtitle, unit.translations, column, 'subtitle');
            const primary = !both || column === null;
            // feat-302: the chip lives in the primary (English, in Both) cell only.
            const chipEditable = !!editApi && column === null;
            const showChip = primary && (chipEditable ? showEditableChip : showReadonlyChip);
            const withToggle = primary && !!toggle;
            return (
              <div className={showChip ? bannerGrid(withToggle) : 'flex items-start gap-2'}>
                {primary && toggle && (
                  <DisclosureButton
                    open={open}
                    onToggle={() => toggle(key)}
                    controls={regionId(key)}
                    title={englishName}
                    className="-ml-1 mt-0.5 w-7 h-7 text-sky-800 hover:bg-sky-100"
                    iconClassName="w-4 h-4"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className={primary && editedOut ? 'opacity-60' : undefined}>
                  {editApi && column === null ? (
                    // feat-299: English title + subtitle are click-to-edit
                    // (Lesson = `updateUnit`). The chip sits before `(inactive)`.
                    <>
                      <EditLine
                        as="h3"
                        className="text-xl font-semibold text-ink break-words focus:outline-none"
                        headingProps={{
                          tabIndex: primary ? -1 : undefined,
                          'data-nav-heading': primary ? '' : undefined,
                        }}
                        prefix={
                          <>
                            {unit.emoji && <span className="mr-2">{unit.emoji}</span>}
                            {COPY.lessonHeading(index)}
                          </>
                        }
                      >
                        <InlineEditable
                          value={unit.title ?? ''}
                          field="title"
                          testId={`inline-lesson-title-${unit.id}`}
                          label={FIELD_LABELS.lessonTitle()}
                          required
                          onSave={(next) => editApi.saveUnit(unit.id, { title: next })}
                        />
                        <LessonPendingChip id={unit.id} />
                        {!unit.active && (
                          <span className="ml-2 text-sm font-normal text-inkMuted">{COPY.inactive}</span>
                        )}
                      </EditLine>
                      <div className="text-sm text-inkMuted mt-0.5">
                        <InlineEditable
                          value={unit.subtitle ?? ''}
                          field="subtitle"
                          testId={`inline-lesson-subtitle-${unit.id}`}
                          label={FIELD_LABELS.lessonSubtitle()}
                          placeholder={EDIT.placeholderSubtitle}
                          onSave={(next) => editApi.saveUnit(unit.id, { subtitle: next })}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <h3
                        className="text-xl font-semibold text-ink break-words focus:outline-none"
                        tabIndex={primary ? -1 : undefined}
                        data-nav-heading={primary ? '' : undefined}
                      >
                        {/* `unit.emoji` is author-set and NOT in TRANSLATABLE_FIELDS —
                            it renders identically in both columns. */}
                        {unit.emoji && <span className="mr-2">{unit.emoji}</span>}
                        {COPY.lessonHeading(index)} <FieldRun f={title} language={column ?? ''} />
                        {!unit.active && (
                          <span className="ml-2 text-sm font-normal text-inkMuted">{COPY.inactive}</span>
                        )}
                      </h3>
                      {subtitle.text && (
                        <div className="text-sm text-inkMuted mt-0.5">
                          <FieldRun f={subtitle} language={column ?? ''} />
                        </div>
                      )}
                    </>
                  )}
                  {primary && !open && (
                    <p className="mt-1 text-xs font-medium text-inkMuted">{lessonSummary(unit)}</p>
                  )}
                  </div>
                  {primary && editedOut && <EditedOutLine text={editedOut} />}
                </div>
                {showChip && (
                  <div className={chipCell(withToggle, 'sm:mt-1')}>
                    <AudienceChip
                      level="lesson"
                      id={unit.id}
                      name={englishName}
                      audience={audience}
                      editable={chipEditable}
                    />
                  </div>
                )}
              </div>
            );
          }}
        />
      </div>
      <div id={regionId(key)} className={open ? 'pl-1' : undefined}>
        {/* feat-299: every save under here re-reads THIS Lesson's pending set. */}
        <LessonIdContext.Provider value={unit.id}>
          {open &&
            unit.drills.map((d, i) => (
              <DrillSection key={d.id} drill={d} index={i + 1} layout={layout} unitId={unit.id} />
            ))}
        </LessonIdContext.Provider>
        {open && unit.drills.length === 0 && (
          <div className="text-sm text-inkMuted italic">{COPY.emptyLesson}</div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------ Stub (NOTES §7)

/**
 * A later occurrence of a multi-Track Lesson: a pointer, never content.
 * NO `data-item-key` (spec §1, AT-3) and no nav target — the jump goes to the
 * full rendering under its owner Track.
 */
const LessonStub: React.FC<{ stub: StubPlacement; layout: Layout }> = ({ stub, layout }) => {
  const { jumpTo } = useContext(SectionControlsContext);
  const { lesson } = stub;
  // A pointer, not content: English in Both mode, and never split.
  const column = layout.mode === 'both' ? null : layout.column;
  const title = resolveField(lesson.title, lesson.translations, column, 'title');
  return (
    <button
      type="button"
      data-testid="lesson-stub"
      onClick={() => jumpTo(lessonKey(lesson.id))}
      className={`group w-full mb-4 flex items-center gap-3 text-left rounded-r border border-sky-200 border-l-4 border-l-sky-300 bg-white px-4 py-2.5 hover:bg-sky-50 ${FOCUS_RING}`}
    >
      {lesson.emoji && <span className="text-lg">{lesson.emoji}</span>}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-ink break-words">
          {COPY.lessonHeading(stub.number)} <FieldRun f={title} language={column ?? ''} />
        </span>
        <span className="block text-sm text-inkMuted">
          {COPY.shownUnder} <span className="font-semibold text-ink">{stub.ownerTitle}</span>
        </span>
      </span>
      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-sky-800 group-hover:underline">
        <span className="hidden sm:inline">{COPY.goToLesson}</span>
        <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
      </span>
    </button>
  );
};

// ------------------------------------------------------------ Track (NOTES §5)

const TrackSection: React.FC<{ group: LessonGroup; layout: Layout }> = ({ group, layout }) => {
  const { isOpen, toggle } = useContext(SectionControlsContext);
  const key = trackKey(group.id);
  const open = isOpen(key);
  const unassigned = group.kind === 'unassigned';
  const name = unassigned ? COPY.unassignedTitle : group.title;
  // feat-302: Track titles are English in every mode, so the chip is editable
  // in EN and Both and read-only only in ES-only (design Q1). No chip on the
  // "Not in any Track" group.
  const audienceApi = useAudience();
  const audience = audienceOf(group);
  const chipEditable = layout.mode === 'both' || layout.column === null;
  const showChipBase = useShowChip(audience, chipEditable);
  const showChip = !unassigned && showChipBase;
  const editedOut = unassigned ? null : audienceApi?.editedOutLine('track', group.id) ?? null;
  const withToggle = !!toggle;
  return (
    <section className="mb-10">
      <div
        data-item-key={key}
        {...{ [NAV_TARGET_ATTR]: key }}
        className={`${
          showChip ? `group/banner ${bannerGrid(withToggle)}` : 'flex items-start gap-2'
        } pt-1 pb-3 mb-5 border-b-2 border-ink/15${
          unassigned ? ' border-dashed' : ''
        } ${SCROLL_MT}`}
      >
        {toggle && (
          <DisclosureButton
            open={open}
            onToggle={() => toggle(key)}
            controls={regionId(key)}
            title={name}
            className="mt-[22px] w-8 h-8 -ml-1 text-inkMuted hover:text-ink hover:bg-cream-dark"
            iconClassName="w-5 h-5"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className={editedOut ? 'opacity-60' : undefined}>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] text-inkMuted"
            aria-hidden={unassigned || undefined}
          >
            {/* The unassigned group keeps an EMPTY eyebrow line so the chevron
                alignment holds, and so it never reads as a real Track. */}
            {unassigned ? ' ' : COPY.trackEyebrow}
          </div>
          {/* Track titles are English in every mode and never split (spec §1). */}
          <h2
            tabIndex={-1}
            data-nav-heading=""
            className={`font-display text-2xl break-words focus:outline-none ${
              unassigned
                ? 'font-medium italic text-inkMuted'
                : `font-semibold ${group.active ? 'text-ink' : 'text-inkMuted'}`
            }`}
          >
            {name}
            {!group.active && !unassigned && (
              <span className="ml-2 align-middle font-sans text-sm font-normal text-inkMuted">
                {COPY.inactive}
              </span>
            )}
          </h2>
          {unassigned && <p className="text-sm text-inkMuted mt-0.5">{COPY.unassignedHelper}</p>}
          {!open && <p className="mt-1 text-sm font-medium text-inkMuted">{trackSummary(group)}</p>}
          </div>
          {editedOut && <EditedOutLine text={editedOut} />}
        </div>
        {showChip && (
          <div className={chipCell(withToggle, 'sm:mt-[26px]')}>
            <AudienceChip
              level="track"
              id={group.id}
              name={group.title}
              audience={audience}
              editable={chipEditable}
            />
          </div>
        )}
      </div>
      <div id={regionId(key)}>
        {open &&
          group.entries.map((entry) =>
            entry.kind === 'full' ? (
              <LessonSection
                key={entry.lesson.id}
                unit={entry.lesson}
                index={entry.number}
                layout={layout}
              />
            ) : (
              <LessonStub key={`stub-${entry.lesson.id}`} stub={entry} layout={layout} />
            ),
          )}
        {open && group.entries.length === 0 && (
          <div className="text-sm text-inkMuted italic">
            {audienceApi?.viewName
              ? AUDIENCE_COPY.trackEmptyForView(audienceApi.viewName)
              : COPY.emptyTrack}
          </div>
        )}
      </div>
    </section>
  );
};

// ------------------------------------------------------------ the body

export const ReviewBody: React.FC<{ grouped: GroupedCurriculum; layout: Layout }> = ({
  grouped,
  layout,
}) => {
  if (grouped.mode === 'flat') {
    return (
      <>
        {grouped.lessons.map((u, i) => (
          <LessonSection key={u.id} unit={u} index={grouped.numbers?.[i] ?? i + 1} layout={layout} />
        ))}
      </>
    );
  }
  return (
    <>
      {grouped.groups.map((g) => (
        <TrackSection key={g.id} group={g} layout={layout} />
      ))}
    </>
  );
};
